/**
 * QuickCreateCustomerDialog — criar cliente sem sair da conversa (D10).
 *
 * Só o mínimo pra conseguir vincular: nome (obrigatório), tipo PF/PJ
 * (obrigatório) e documento (opcional, com a mesma máscara do cadastro
 * completo). Endereço, segmento, site e domínios continuam na página inteira,
 * acessível depois — ver abaixo.
 *
 * Modal shared = tema claro fixo por dentro (o próprio `Modal` sobrescreve as
 * CSS variables), e no celular ele já vira bottom sheet abaixo de 640px.
 *
 * Quem vincula ao contato é o chamador, pelo `onCreated`.
 *
 * Sem link pro cadastro completo aqui dentro (decisão 2026-08-20, correção
 * 2/5): `handleSubmit` chama `onClose()` logo depois de `onCreated()`, então
 * um link no rodapé deste modal ficaria inalcançável (some antes do
 * atendente conseguir clicar). E o link "antes de criar" pra `/crm/customers/new`
 * tirava o atendente da conversa — o oposto do que o atalho existe pra
 * evitar. O caminho pro cadastro completo agora mora no RODAPÉ DO PAINEL do
 * seletor (`ConversationCustomerSelector`, item "Abrir cadastro completo",
 * `data-testid="chat-customer-open-full"`) — aparece sempre que já existe
 * cliente vinculado, descobrível a qualquer momento, não só nos milissegundos
 * depois de criar.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import {
  Button, FormField, Input, Modal, ModalContent, ModalFooter, ModalHeader, Select,
} from '@/shared/components/ui';
import { useCreateCustomer } from '@/modules/crm/customers/hooks';
import { formatDocumentInput, documentLabel } from '@/shared/utils/documentMask';

import { buildQuickCustomerPayload, validateQuickCustomer } from '../utils/quickCustomerPayload';

const TYPE_OPTIONS = [
  { value: 'PJ', label: 'Pessoa Jurídica (PJ)' },
  { value: 'PF', label: 'Pessoa Física (PF)' },
];

interface QuickCreateCustomerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (customer: { id: string; name: string }) => void;
  initialName?: string;
}

const QuickCreateCustomerDialog: React.FC<QuickCreateCustomerDialogProps> = ({
  isOpen, onClose, onCreated, initialName,
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'PF' | 'PJ'>('PJ');
  const [document, setDocument] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);

  // `silent: true` — o `useCreateCustomer` base toasta "Cliente criado com
  // sucesso" sozinho; aqui quem cria TAMBÉM vincula na sequência (via
  // `onCreated` → `applyCustomer` no `ConversationCustomerSelector`), que
  // dispara o toast "Cliente {name} criado e vinculado". Sem silenciar, dois
  // toasts empilhavam em 100% das criações pelo atalho (correção 2026-08-20).
  const createMutation = useCreateCustomer({ silent: true });

  // Abrir recomeça do zero, com o termo já digitado na busca virando o nome.
  useEffect(() => {
    if (!isOpen) return;
    setName(initialName?.trim() ?? '');
    setType('PJ');
    setDocument('');
    setNameError(null);
    setDocumentError(null);
  }, [isOpen, initialName]);

  const handleTypeChange = (value: string) => {
    const next: 'PF' | 'PJ' = value === 'PF' ? 'PF' : 'PJ';
    setType(next);
    // Reaplicar a máscara corta os dígitos excedentes quando cai de PJ pra PF.
    setDocument((prev) => formatDocumentInput(prev, next));
    setDocumentError(null);
  };

  const handleSubmit = async () => {
    const invalid = validateQuickCustomer({ name, type, document });
    if (invalid) {
      if (invalid.field === 'name') setNameError(invalid.message);
      else setDocumentError(invalid.message);
      return;
    }

    try {
      const createdCustomer = await createMutation.mutateAsync(buildQuickCustomerPayload({ name, type, document }));
      onCreated({ id: createdCustomer.id, name: createdCustomer.name });
      onClose();
    } catch (error) {
      // 409 = documento já cadastrado no tenant; vira erro do campo, não toast.
      // O backend serializa `ConflictError` em INGLÊS ("A customer with
      // document ... already exists") — `message` do response é SEMPRE
      // truthy, então um fallback `message || '...'` nunca cai no pt-BR.
      // Ignorar o texto do backend e usar sempre a mensagem fixa (mesmo
      // sintoma do `CustomerFormPage.tsx`, que não mexemos: é outra tela).
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setDocumentError('Documento já cadastrado');
        return;
      }
      toast.error('Erro ao criar cliente');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <ModalHeader title="Novo cliente" onClose={onClose} />
      <ModalContent>
        <div className="space-y-4" data-testid="quick-customer-dialog">
          <FormField label="Nome" required error={nameError ?? undefined}>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(null); }}
              placeholder={type === 'PF' ? 'Ex: João da Silva' : 'Ex: Acme Comércio Ltda'}
              autoFocus
              data-testid="quick-customer-name"
            />
          </FormField>

          <FormField label="Tipo" required>
            <Select
              options={TYPE_OPTIONS}
              value={type}
              onChange={handleTypeChange}
              data-testid="quick-customer-type"
            />
          </FormField>

          <FormField label={documentLabel(type)} error={documentError ?? undefined}>
            <Input
              value={document}
              onChange={(e) => { setDocument(formatDocumentInput(e.target.value, type)); setDocumentError(null); }}
              placeholder={type === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
              inputMode="numeric"
              data-testid="quick-customer-document"
            />
          </FormField>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          onClick={() => { void handleSubmit(); }}
          loading={createMutation.isPending}
          data-testid="quick-customer-submit"
        >
          Criar e vincular
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default QuickCreateCustomerDialog;
export { QuickCreateCustomerDialog };
